import { Readable } from 'node:stream';
import {
  ReadableStream as _ReadableStream,
  type ReadableStreamController,
  TransformStream,
} from 'node:stream/web';
import type { Agent, AgentOutputType } from './agent';
import type { AgentToolInvocation } from './agent-tool-invocation';
import type { RunStreamEvent } from './events';
import type { InputGuardrailResult, OutputGuardrailResult } from './guardrail';
import type { Handoff } from './handoff';
import type { RunItem, RunToolApprovalItem } from './items';
import logger from './logger';
import type { ModelResponse } from './model';
import type { RunContext } from './run-context';
import type { RunState } from './run-state';
import { getTurnInput } from './runner/items';
import { type ReadableStream } from './shims/interface';
import type {
  ToolInputGuardrailResult,
  ToolOutputGuardrailResult,
} from './tool-guardrails';
import type {
  AgentInputItem,
  AgentOutputItem,
  HandoffsOutput,
  ResolvedAgentOutput,
} from './types';
import { StreamEventTextStream } from './types/protocol';
import { combineAbortSignalsWithOptions } from './utils/abort-signals';

type AbortHandlerRef<T extends object> = { current?: T };

function createAbortHandlerRef(): {
  ref: AbortHandlerRef<() => void>;
  handler: () => void;
} {
  const ref: AbortHandlerRef<() => void> = {};
  const handler = () => {
    ref.current?.();
  };
  return { ref, handler };
}

/**
 * Agent 的 run() 方法返回的数据。
 */
export interface RunResultData<
  TAgent extends Agent<any, any>,
  THandoffs extends (Agent<any, any> | Handoff<any>)[] = any[],
> {
  /**
   * 原始输入项，即调用 run() 之前的输入项。如果存在交接输入过滤器对输入进行了修改，
   * 则该值可能是经过修改后的版本。
   */
  input: string | AgentInputItem[];

  /**
   * Agent 运行期间生成的新项。包括新消息、工具调用及其输出等内容。
   */
  newItems: RunItem[];

  /**
   * Agent 运行期间模型生成的原始 LLM 响应。
   */
  rawResponses: ModelResponse[];

  /**
   * Agent 运行期间模型生成的最后一条响应 ID。
   */
  lastResponseId: string | undefined;

  /**
   * 最后运行的 Agent。
   */
  lastAgent: TAgent | undefined;

  /**
   * 对输入消息的防护栏（guardrail）检查结果。
   */
  inputGuardrailResults: InputGuardrailResult[];

  /**
   * 对 Agent 最终输出的防护栏（guardrail）检查结果。
   */
  outputGuardrailResults: OutputGuardrailResult[];

  /**
   * 运行期间对工具输入的防护栏（guardrail）检查结果。
   */
  toolInputGuardrailResults: ToolInputGuardrailResult[];

  /**
   * 运行期间对工具输出的防护栏（guardrail）检查结果。
   */
  toolOutputGuardrailResults: ToolOutputGuardrailResult[];

  /**
   * 最后一个 Agent 或任意交接 Agent 的输出。
   */
  finalOutput?:
    | ResolvedAgentOutput<TAgent['outputType']>
    | HandoffsOutput<THandoffs>;

  /**
   * Agent 运行期间发生的中断。
   */
  interruptions?: RunToolApprovalItem[];

  /**
   * 运行的状态。
   */
  state: RunState<any, TAgent>;

  /**
   * 本次运行的公共运行上下文。
   */
  runContext: RunContext<any>;

  /**
   * 关于生成此结果的嵌套 `Agent.asTool()` 调用的元数据（如适用）。
   */
  agentToolInvocation?: AgentToolInvocation;
}

class RunResultBase<TContext, TAgent extends Agent<TContext, any>>
  implements RunResultData<TAgent>
{
  readonly state: RunState<TContext, TAgent>;

  constructor(state: RunState<TContext, TAgent>) {
    this.state = state;
  }

  /**
   * Agent 运行的历史记录。包含输入项以及 Agent 运行期间生成的新项。
   *
   * 可作为下一次 Agent 运行的输入使用。
   */
  get history(): AgentInputItem[] {
    return getTurnInput(
      this.input,
      this.newItems,
      this.state._reasoningItemIdPolicy,
    );
  }

  /**
   * Agent 运行期间生成的新项。包括新消息、工具调用及其输出等内容。
   *
   * 不包含与 Agent 相关的信息，仅表示模型数据。
   *
   * 如需包含 Agent 信息的输出，请使用 `newItems` 属性。
   */
  get output(): AgentOutputItem[] {
    return getTurnInput([], this.newItems, this.state._reasoningItemIdPolicy);
  }

  /**
   * 原始输入项的副本。
   */
  get input(): string | AgentInputItem[] {
    return this.state._originalInput;
  }

  /**
   * 本次运行的公共运行上下文。
   */
  get runContext(): RunContext<TContext> {
    return this.state._context;
  }

  /**
   * 关于生成此结果的嵌套 `Agent.asTool()` 调用的元数据（如适用）。
   */
  get agentToolInvocation(): AgentToolInvocation | undefined {
    return this.state._agentToolInvocation;
  }

  /**
   * Agent 运行期间生成的运行项。将模型数据与 Agent 关联起来。
   *
   * 如需获取可作为下一次 Agent 运行输入的模型数据，请使用 `output` 属性。
   */
  get newItems(): RunItem[] {
    return this.state._generatedItems;
  }

  /**
   * Agent 运行期间模型生成的原始 LLM 响应。
   */
  get rawResponses(): ModelResponse[] {
    return this.state._modelResponses;
  }

  /**
   * Agent 运行期间模型生成的最后一条响应 ID。
   */
  get lastResponseId(): string | undefined {
    const responses = this.rawResponses;
    return responses && responses.length > 0
      ? responses[responses.length - 1]!.responseId
      : undefined;
  }

  /**
   * 最后运行的 Agent。
   */
  get lastAgent(): TAgent | undefined {
    return this.state._currentAgent;
  }

  /**
   * 应处理下一轮的 Agent。
   * 这是完成上一轮的最后一个 Agent 的别名。
   */
  get activeAgent(): TAgent | undefined {
    return this.lastAgent;
  }

  /**
   * 对输入消息的防护栏（guardrail）检查结果。
   */
  get inputGuardrailResults(): InputGuardrailResult[] {
    return this.state._inputGuardrailResults;
  }

  /**
   * 对 Agent 最终输出的防护栏（guardrail）检查结果。
   */
  get outputGuardrailResults(): OutputGuardrailResult[] {
    return this.state._outputGuardrailResults;
  }

  /**
   * 对工具输入的防护栏（guardrail）检查结果。
   */
  get toolInputGuardrailResults(): ToolInputGuardrailResult[] {
    return this.state._toolInputGuardrailResults;
  }

  /**
   * 对工具输出的防护栏（guardrail）检查结果。
   */
  get toolOutputGuardrailResults(): ToolOutputGuardrailResult[] {
    return this.state._toolOutputGuardrailResults;
  }

  /**
   * Agent 运行期间发生的所有中断，例如工具审批引发的中断。
   */
  get interruptions(): RunToolApprovalItem[] {
    if (this.state._currentStep?.type === 'next_step_interruption') {
      return this.state._currentStep.data.interruptions;
    }

    return [];
  }

  /**
   * Agent 的最终输出。如果输出类型设置为 `text` 以外的其他类型，
   * 则此属性将被解析为 JSON，或使用你所提供的 Zod Schema 进行解析。
   */
  get finalOutput(): ResolvedAgentOutput<TAgent['outputType']> | undefined {
    if (this.state._currentStep?.type === 'next_step_final_output') {
      return this.state._currentAgent.processFinalOutput(
        this.state._currentStep.output,
      ) as ResolvedAgentOutput<TAgent['outputType']>;
    }

    logger.warn('Accessed finalOutput before agent run is completed.');
    return undefined;
  }
}

/**
 * The result of an agent run.
 */
export class RunResult<
  T,
  P extends Agent<T, AgentOutputType>,
> extends RunResultBase<T, P> {
  constructor(state: RunState<T, P>) {
    super(state);
  }
}

/**
 * The result of an agent run in streaming mode.
 */
export class StreamedRunResult<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
  >
  extends RunResultBase<TContext, TAgent>
  implements AsyncIterable<RunStreamEvent>
{
  /**
   * The current agent that is running
   */
  public get currentAgent(): TAgent | undefined {
    return this.lastAgent;
  }

  /**
   * The current turn number
   */
  public currentTurn: number = 0;

  /**
   * The maximum number of turns that can be run
   */
  public maxTurns: number | null | undefined;

  #error: unknown = null;
  #combinedSignal?: AbortSignal;
  #externalSignal?: AbortSignal;
  #abortSignalSnapshot?: AbortSignal;
  #abortController: AbortController;
  #readableController: ReadableStreamController<RunStreamEvent> | undefined;
  #readableStream: _ReadableStream<RunStreamEvent>;
  #completedPromise: Promise<void>;
  #completedPromiseResolve: (() => void) | undefined;
  #completedPromiseReject: ((err: unknown) => void) | undefined;
  #cancelled: boolean = false;
  #streamLoopPromise: Promise<void> | undefined;
  #abortHandler: (() => void) | undefined;
  #abortHandlerRef: AbortHandlerRef<() => void> | undefined;
  #combinedSignalCleanup: () => void = () => {};

  constructor(
    result: {
      state: RunState<TContext, TAgent>;
      signal?: AbortSignal;
    } = {} as any,
  ) {
    super(result.state);

    this.#externalSignal = result.signal;
    this.#abortController = new AbortController();
    const { signal: combinedSignal, cleanup: cleanupCombinedSignal } =
      combineAbortSignalsWithOptions(
        [result.signal, this.#abortController.signal],
        {
          onAbortSignalAnyError: (error) => {
            logger.debug(`AbortSignal.any failed, falling back: ${error}`);
          },
        },
      );
    this.#combinedSignal = combinedSignal;
    this.#combinedSignalCleanup = cleanupCombinedSignal;
    this.#abortSignalSnapshot = combinedSignal;

    this.#readableStream = new _ReadableStream<RunStreamEvent>({
      start: (controller) => {
        this.#readableController = controller;
      },
      cancel: () => {
        if (!this.#abortController.signal.aborted) {
          this.#abortController.abort();
        }
      },
    });

    this.#completedPromise = new Promise((resolve, reject) => {
      this.#completedPromiseResolve = resolve;
      this.#completedPromiseReject = reject;
    });

    if (this.#combinedSignal) {
      // Create the handler outside this scope so it cannot capture the run via lexical this.
      const { ref: abortRef, handler: handleAbort } = createAbortHandlerRef();
      abortRef.current = () => {
        this.#handleAbort();
      };
      this.#abortHandler = handleAbort;
      this.#abortHandlerRef = abortRef;

      if (this.#combinedSignal.aborted) {
        handleAbort();
      } else {
        this.#combinedSignal.addEventListener('abort', handleAbort, {
          once: true,
        });
      }
    }
  }

  /**
   * @internal
   * Adds an item to the stream of output items
   */
  _addItem(item: RunStreamEvent) {
    if (!this.cancelled) {
      this.#readableController?.enqueue(item);
    }
  }

  /**
   * @internal
   * Indicates that the stream has been completed
   */
  _done() {
    if (!this.cancelled && this.#readableController) {
      this.#readableController.close();
      this.#readableController = undefined;
      this.#completedPromiseResolve?.();
    }
    this.#detachAbortHandler();
  }

  /**
   * @internal
   * Handles an error in the stream loop.
   */
  _raiseError(err: unknown) {
    if (!this.cancelled && this.#readableController) {
      this.#readableController.error(err);
      this.#readableController = undefined;
    }
    this.#error = err;
    this.#completedPromiseReject?.(err);
    this.#completedPromise.catch((e) => {
      logger.debug(`Resulted in an error: ${e}`);
    });
    this.#detachAbortHandler();
  }

  /**
   * Returns true if the stream has been cancelled.
   */
  get cancelled(): boolean {
    return this.#cancelled;
  }

  /**
   * Returns the underlying readable stream.
   * @returns A readable stream of the agent run.
   */
  toStream(): ReadableStream<RunStreamEvent> {
    return this.#readableStream as ReadableStream<RunStreamEvent>;
  }

  /**
   * Await this promise to ensure that the stream has been completed if you are not consuming the
   * stream directly.
   */
  get completed() {
    return this.#completedPromise;
  }

  /**
   * Error thrown during the run, if any.
   */
  get error() {
    return this.#error;
  }

  /**
   * Returns a readable stream of the final text output of the agent run.
   *
   * @returns A readable stream of the final output of the agent run.
   * @remarks Pass `{ compatibleWithNodeStreams: true }` to receive a Node.js compatible stream
   * instance.
   */
  toTextStream(): ReadableStream<string>;
  toTextStream(options?: { compatibleWithNodeStreams: true }): Readable;
  toTextStream(options?: {
    compatibleWithNodeStreams?: false;
  }): ReadableStream<string>;
  toTextStream(
    options: { compatibleWithNodeStreams?: boolean } = {},
  ): Readable | ReadableStream<string> {
    const stream = this.#readableStream.pipeThrough(
      new TransformStream<RunStreamEvent, string>({
        transform(event, controller) {
          if (
            event.type === 'raw_model_stream_event' &&
            event.data.type === 'output_text_delta'
          ) {
            const item = StreamEventTextStream.parse(event.data);
            controller.enqueue(item.delta);
          }
        },
      }),
    );

    if (options.compatibleWithNodeStreams) {
      return Readable.fromWeb(stream);
    }

    return stream as ReadableStream<string>;
  }

  [Symbol.asyncIterator](): AsyncIterator<RunStreamEvent> {
    return this.#readableStream[Symbol.asyncIterator]();
  }

  /**
   * @internal
   * Sets the stream loop promise that completes when the internal stream loop finishes.
   * This is used to defer trace end until all agent work is complete.
   */
  _setStreamLoopPromise(promise: Promise<void>) {
    this.#streamLoopPromise = promise;
  }

  /**
   * @internal
   * Returns a promise that resolves when the stream loop completes.
   * This is used by the tracing system to wait for all agent work before ending the trace.
   */
  _getStreamLoopPromise(): Promise<void> | undefined {
    return this.#streamLoopPromise;
  }

  /**
   * @internal
   * Returns the abort signal that should be used to cancel the streaming run.
   */
  _getAbortSignal(): AbortSignal | undefined {
    return this.#abortSignalSnapshot ?? this.#combinedSignal;
  }

  /**
   * @internal
   * Returns whether the caller-provided signal was aborted, even if stream consumption was also
   * cancelled locally.
   */
  _isExternalSignalAborted(): boolean {
    return this.#externalSignal?.aborted ?? false;
  }

  #handleAbort() {
    if (this.#cancelled) {
      this.#detachAbortHandler();
      return;
    }

    this.#cancelled = true;

    const controller = this.#readableController;
    this.#readableController = undefined;

    if (controller) {
      try {
        controller.close();
      } catch (err) {
        logger.debug(`Failed to close readable stream on abort: ${err}`);
      }
    }

    this.#completedPromiseResolve?.();
    this.#detachAbortHandler();
  }

  #detachAbortHandler() {
    // Clear the indirection first so a retained signal cannot keep this run alive.
    if (this.#abortHandlerRef) {
      this.#abortHandlerRef.current = undefined;
    }
    if (this.#combinedSignal && this.#abortHandler) {
      try {
        this.#combinedSignal.removeEventListener('abort', this.#abortHandler);
      } catch (err) {
        logger.debug(`Failed to remove abort listener: ${err}`);
      }
    }
    try {
      this.#combinedSignalCleanup();
    } catch (err) {
      logger.debug(`Failed to clean up combined abort listeners: ${err}`);
    }
    this.#combinedSignalCleanup = () => {};
    this.#abortHandler = undefined;
    this.#abortHandlerRef = undefined;
  }
}
